import { createContext, useContext, useReducer, type ActionDispatch, type ReactNode } from 'react';
import type { UserResponseAPI } from '../types/user';

type UserProviderProps = {
    user: UserResponseAPI;
    dispatchUser?: (() => void) | undefined;
    children: ReactNode;
};

const UserContext = createContext<UserResponseAPI | null>(null);
const UserDispatchContext = createContext<ActionDispatch<[action: UserAction]> | null>(null);

export type UserAction = { type: 'setUser'; payload: UserResponseAPI };

const userReducer = (user: UserResponseAPI, action: UserAction): UserResponseAPI => {
    switch (action.type) {
        case 'setUser':
            return action.payload;
        default:
            return user;
    }
};

const UserProvider = ({
    user: initialUser,
    dispatchUser: mockedDispatch,
    children,
}: UserProviderProps) => {
    const [user, dispatchUser] = useReducer(userReducer, initialUser);

    return (
        <UserContext.Provider value={user}>
            <UserDispatchContext.Provider value={mockedDispatch ?? dispatchUser}>
                {children}
            </UserDispatchContext.Provider>
        </UserContext.Provider>
    );
};

const useUser = (): UserResponseAPI => {
    const context = useContext(UserContext);
    if (context === null) {
        throw new Error('useUser must be used within a UserProvider');
    }
    return context;
};

const useUserDispatch = (): ActionDispatch<[action: UserAction]> => {
    const context = useContext(UserDispatchContext);
    if (context === null) {
        throw new Error('useUserDispatch must be used within a UserProvider');
    }
    return context;
};

export { UserProvider, useUser, useUserDispatch };
